import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import Navbar from "../../components/Navbar";
import { bookingsAPI, roomsAPI } from "../../services/meetingsAPI";
import {
  clearPendingRange,
  forEachSlotInRange,
  readRoomPendingHolds,
  removeRoomPendingHold,
  syncPendingHoldKeysFromStorage,
  writeRoomPendingHold,
} from "../../utils/schedulePendingStorage";
import "./RoomAvailabilitySchedulePage.css";

const SLOT_INTERVAL_MINUTES = 30;
const DAYS_PER_VIEW = 7;
const TIME_SLOTS = Array.from({ length: (24 * 60) / SLOT_INTERVAL_MINUTES }, (_, index) => {
  const totalMinutes = index * SLOT_INTERVAL_MINUTES;
  const hour = Math.floor(totalMinutes / 60);
  const minute = totalMinutes % 60;
  const hour24 = String(hour).padStart(2, "0");
  const minutePart = String(minute).padStart(2, "0");
  const suffix = hour < 12 ? "AM" : "PM";
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  const minuteLabel = minute === 0 ? "00" : "30";
  return {
    hour,
    minute,
    key: `${hour24}:${minutePart}`,
    label: `${hour12}:${minuteLabel} ${suffix}`,
  };
});

const toYmd = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const parseYmd = (ymd) => {
  const [year, month, day] = ymd.split("-").map(Number);
  return new Date(year, month - 1, day);
};

const addDaysToYmd = (ymd, days) => {
  const date = parseYmd(ymd);
  date.setDate(date.getDate() + days);
  return toYmd(date);
};

const buildDateItem = (ymd) => {
  const date = parseYmd(ymd);
  return {
    value: ymd,
    shortLabel: date.toLocaleDateString("en-GB", { day: "numeric", month: "short" }),
    dayName: date.toLocaleDateString("en-GB", { weekday: "short" }),
  };
};

const parseDateValue = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const normalizeBookingStatus = (booking) => {
  if (!booking || typeof booking !== "object") return null;

  const status = String(booking.status || "").toLowerCase().trim();
  const hostDecision = String(booking.hostDecision || "").toLowerCase().trim();

  if (status === "cancelled" || status === "declined") return null;

  // User booked — owner has not approved yet
  if (status === "pending") return "pending";

  // Owner approved (or auto-confirmed when approval is not required)
  if (
    status === "confirmed" ||
    status === "approved" ||
    hostDecision === "approved"
  ) {
    return "booked";
  }

  return null;
};

const collectBookings = (payload) => {
  const bookings = [];
  const seen = new Set();

  const inspectBooking = (booking) => {
    if (!booking || typeof booking !== "object") return;
    const bookingId = String(booking._id || booking.id || "");
    if (bookingId && seen.has(bookingId)) return;

    const startTime = booking.startTime || booking.checkIn || booking.start;
    const endTime = booking.endTime || booking.checkOut || booking.end;
    const slotStatus = normalizeBookingStatus(booking);
    if (!startTime || !endTime || !slotStatus) return;

    if (bookingId) seen.add(bookingId);
    bookings.push({
      startTime,
      endTime,
      status: slotStatus,
      ownerApprovalDeadline: booking.ownerApprovalDeadline,
    });
  };

  const inspectNode = (node) => {
    if (!node) return;
    if (Array.isArray(node)) {
      node.forEach(inspectNode);
      return;
    }
    if (typeof node !== "object") return;

    if (node.startTime || node.checkIn || node.start) {
      inspectBooking(node);
    }

    if (Array.isArray(node.bookings)) node.bookings.forEach(inspectBooking);
    if (Array.isArray(node.data)) node.data.forEach(inspectNode);
    if (Array.isArray(node.results)) node.results.forEach(inspectNode);
    if (node.booking) inspectBooking(node.booking);
  };

  inspectNode(payload);
  return bookings;
};

const markSlotsForBookingRange = (slotMap, holdKeys, roomId, booking, status) => {
  const bookingStart = parseDateValue(booking.startTime);
  const bookingEnd = parseDateValue(booking.endTime);
  if (!bookingStart || !bookingEnd) return;

  forEachSlotInRange(bookingStart, bookingEnd, (dateYmd, timeKey) => {
    const key = `${dateYmd}|${timeKey}`;

    if (status === "pending") {
      slotMap.set(key, "pending");
      holdKeys.add(key);
      writeRoomPendingHold(roomId, dateYmd, timeKey);
      return;
    }

    if (status === "booked") {
      slotMap.set(key, "booked");
      holdKeys.delete(key);
      removeRoomPendingHold(roomId, dateYmd, timeKey);
    }
  });

  if (status === "booked") {
    clearPendingRange(roomId, bookingStart, bookingEnd);
  }
};

const toSlotDate = (dateYmd, timeKey) => {
  const [year, month, day] = dateYmd.split("-").map(Number);
  const [hours, minutes] = timeKey.split(":").map(Number);
  return new Date(year, month - 1, day, hours, minutes, 0, 0);
};

const getEntityId = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  return String(value._id || value.id || value);
};

const RoomAvailabilitySchedulePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const gridScrollRef = useRef(null);
  const hasLoadedScheduleRef = useRef(false);
  const pendingHoldKeysRef = useRef(new Set());

  const todayYmd = useMemo(() => toYmd(new Date()), []);

  const [weekStartYmd, setWeekStartYmd] = useState(() => {
    const dateParam = searchParams.get("date");
    const today = toYmd(new Date());
    if (dateParam && /^\d{4}-\d{2}-\d{2}$/.test(dateParam) && dateParam >= today) {
      return dateParam;
    }
    return today;
  });

  const [rooms, setRooms] = useState([]);
  const [selectedRoomId, setSelectedRoomId] = useState(searchParams.get("roomId") || "");
  const [slotStatuses, setSlotStatuses] = useState(() => new Map());
  const [loading, setLoading] = useState(true);

  const currentCompanyId = useMemo(() => {
    try {
      const raw = localStorage.getItem("user");
      const user = raw ? JSON.parse(raw) : null;
      return getEntityId(user?.company || user?.companyId);
    } catch {
      return "";
    }
  }, []);

  useEffect(() => {
    if (weekStartYmd < todayYmd) setWeekStartYmd(todayYmd);
  }, [weekStartYmd, todayYmd]);

  const displayedDates = useMemo(
    () =>
      Array.from({ length: DAYS_PER_VIEW }, (_, index) =>
        buildDateItem(addDaysToYmd(weekStartYmd, index))
      ),
    [weekStartYmd]
  );

  const weekRangeLabel = useMemo(() => {
    if (!displayedDates.length) return "No dates";
    return `${displayedDates[0].shortLabel} – ${displayedDates[displayedDates.length - 1].shortLabel}`;
  }, [displayedDates]);

  const monthTitle = useMemo(() => {
    return parseYmd(weekStartYmd).toLocaleDateString("en-GB", {
      month: "long",
      year: "numeric",
    });
  }, [weekStartYmd]);

  const monthKey = useMemo(() => weekStartYmd.slice(0, 7), [weekStartYmd]);
  const canPrevWeek = weekStartYmd > todayYmd;

  const selectedRoom = useMemo(
    () => rooms.find((room) => room._id === selectedRoomId) || null,
    [rooms, selectedRoomId]
  );

  const slotKey = useCallback((date, time) => `${date}|${time}`, []);
  const isPastSlot = useCallback((date, time) => toSlotDate(date, time) < new Date(), []);

  useEffect(() => {
    const params = { month: monthKey, date: weekStartYmd };
    if (selectedRoomId) params.roomId = selectedRoomId;
    setSearchParams(params, { replace: true });
  }, [monthKey, selectedRoomId, weekStartYmd, setSearchParams]);

  useEffect(() => {
    const fetchRooms = async () => {
      try {
        const response = await roomsAPI.getRooms({
          ownerAddedOnly: true,
          managedOnly: true,
          sort: "latest",
        });
        const data = Array.isArray(response.data) ? response.data : [];
        let user = null;
        try {
          user = JSON.parse(localStorage.getItem("user") || "null");
        } catch {
          user = null;
        }
        const userId = String(user?._id || user?.id || "");
        const hasCompany = Boolean(currentCompanyId);

        const privateWorkspaceRooms = data.filter((room) => {
          const visibility = String(room?.visibility || "private").toLowerCase();
          if (visibility !== "private") return false;
          if (!hasCompany) {
            const owner = room?.userId;
            const ownerId =
              typeof owner === "object"
                ? String(owner._id || owner.id || "")
                : String(owner || "");
            return userId && ownerId === userId;
          }
          return getEntityId(room?.companyId) === currentCompanyId;
        });

        setRooms(privateWorkspaceRooms);
        if (!selectedRoomId && privateWorkspaceRooms.length > 0) {
          setSelectedRoomId(privateWorkspaceRooms[0]._id);
        } else if (
          selectedRoomId &&
          !privateWorkspaceRooms.some((room) => room._id === selectedRoomId) &&
          privateWorkspaceRooms.length > 0
        ) {
          setSelectedRoomId(privateWorkspaceRooms[0]._id);
        }
      } catch (error) {
        console.error("Failed to fetch rooms", error);
        toast.error("Unable to load rooms");
      }
    };
    fetchRooms();
  }, [selectedRoomId, currentCompanyId]);

  const fetchWeekAvailability = useCallback(async () => {
    if (!selectedRoomId || !displayedDates.length) {
      setSlotStatuses(new Map());
      pendingHoldKeysRef.current = new Set();
      setLoading(false);
      return;
    }

    const isFirstLoad = !hasLoadedScheduleRef.current;
    if (isFirstLoad) setLoading(true);

    syncPendingHoldKeysFromStorage(selectedRoomId, pendingHoldKeysRef.current);

    try {
      const dailyResponses = await Promise.all(
        displayedDates.map(async (day) => {
          try {
            const response = await bookingsAPI.getRoomBookings(selectedRoomId, day.value);
            return { ok: true, bookings: collectBookings(response?.data) };
          } catch (error) {
            console.error(`Failed to fetch bookings for ${day.value}`, error);
            return { ok: false, bookings: [] };
          }
        })
      );

      const bookingsById = new Map();
      dailyResponses.forEach((dayResult) => {
        if (!dayResult?.ok) return;
        (dayResult.bookings || []).forEach((booking) => {
          const id = `${booking.startTime}|${booking.endTime}|${booking.status}`;
          if (!bookingsById.has(id)) bookingsById.set(id, booking);
        });
      });

      const displayedDateSet = new Set(displayedDates.map((day) => day.value));

      setSlotStatuses((prev) => {
        const next = new Map(prev);
        const holdKeys = new Set(pendingHoldKeysRef.current);
        syncPendingHoldKeysFromStorage(selectedRoomId, holdKeys);

        displayedDates.forEach((day) => {
          TIME_SLOTS.forEach((slot) => {
            next.delete(slotKey(day.value, slot.key));
          });
        });

        bookingsById.forEach((booking) => {
          if (booking.status === "pending") {
            markSlotsForBookingRange(next, holdKeys, selectedRoomId, booking, "pending");
          } else if (booking.status === "booked") {
            markSlotsForBookingRange(next, holdKeys, selectedRoomId, booking, "booked");
          }
        });

        holdKeys.forEach((key) => {
          if (next.get(key) === "booked") return;
          const [dateYmd] = key.split("|");
          if (!displayedDateSet.has(dateYmd)) return;
          next.set(key, "pending");
        });

        pendingHoldKeysRef.current = holdKeys;
        return next;
      });

      hasLoadedScheduleRef.current = true;
    } finally {
      if (isFirstLoad) setLoading(false);
    }
  }, [displayedDates, selectedRoomId, slotKey]);

  useEffect(() => {
    hasLoadedScheduleRef.current = false;
    pendingHoldKeysRef.current = new Set();
    syncPendingHoldKeysFromStorage(selectedRoomId, pendingHoldKeysRef.current);

    const holds = readRoomPendingHolds(selectedRoomId);
    setSlotStatuses((prev) => {
      const next = new Map(prev);
      Object.entries(holds).forEach(([key, hold]) => {
        if (next.get(key) === "booked") return;
        next.set(key, "pending");
        pendingHoldKeysRef.current.add(key);
        if (hold?.date && hold?.time) {
          writeRoomPendingHold(selectedRoomId, hold.date, hold.time);
        }
      });
      return next;
    });
  }, [selectedRoomId, weekStartYmd]);

  const applyOptimisticPendingHold = useCallback(() => {
    try {
      const raw = sessionStorage.getItem("schedulePendingHold");
      if (!raw) return;
      const hold = JSON.parse(raw);
      if (String(hold.roomId) !== String(selectedRoomId)) return;
      if (!hold.date || !hold.time) return;

      const key = slotKey(hold.date, hold.time);
      pendingHoldKeysRef.current.add(key);
      writeRoomPendingHold(selectedRoomId, hold.date, hold.time);
      setSlotStatuses((prev) => {
        const next = new Map(prev);
        if (next.get(key) !== "booked") {
          next.set(key, "pending");
        }
        return next;
      });
    } catch {
      sessionStorage.removeItem("schedulePendingHold");
    }
  }, [selectedRoomId, slotKey]);

  useEffect(() => {
    applyOptimisticPendingHold();
    fetchWeekAvailability();
  }, [fetchWeekAvailability, location.key, applyOptimisticPendingHold]);

  useEffect(() => {
    const refreshOnFocus = () => {
      if (document.visibilityState === "visible") fetchWeekAvailability();
    };
    window.addEventListener("focus", fetchWeekAvailability);
    document.addEventListener("visibilitychange", refreshOnFocus);
    return () => {
      window.removeEventListener("focus", fetchWeekAvailability);
      document.removeEventListener("visibilitychange", refreshOnFocus);
    };
  }, [fetchWeekAvailability]);

  useEffect(() => {
    try {
      const holdRaw = sessionStorage.getItem("schedulePendingHold");
      if (!holdRaw) return;
      const hold = JSON.parse(holdRaw);
      if (String(hold.roomId) !== String(selectedRoomId)) return;
      const key = slotKey(hold.date, hold.time);
      const status = slotStatuses.get(key);
      if (status === "booked") {
        sessionStorage.removeItem("schedulePendingHold");
        pendingHoldKeysRef.current.delete(key);
        removeRoomPendingHold(selectedRoomId, hold.date, hold.time);
      }
    } catch {
      sessionStorage.removeItem("schedulePendingHold");
    }
  }, [slotStatuses, selectedRoomId, slotKey]);

  useEffect(() => {
    if (loading || !gridScrollRef.current) return;
    const now = new Date();
    const nowMinutes = now.getHours() * 60 + now.getMinutes();
    const targetIndex = TIME_SLOTS.findIndex(
      (slot) => slot.hour * 60 + slot.minute >= nowMinutes
    );
    const scrollIndex = targetIndex >= 0 ? targetIndex : 0;
    gridScrollRef.current.scrollTop = Math.max(0, (scrollIndex - 1) * 52);
  }, [loading, weekStartYmd, selectedRoomId]);

  const moveWeek = (offset) => {
    setWeekStartYmd((prev) => {
      const next = addDaysToYmd(prev, offset * DAYS_PER_VIEW);
      return next < todayYmd ? todayYmd : next;
    });
  };

  const handleBookClick = (date, timeKey) => {
    if (isPastSlot(date, timeKey)) return;
    const existing = slotStatuses.get(slotKey(date, timeKey));
    if (existing === "pending") {
      toast.info("This slot is pending owner approval.");
      return;
    }
    if (existing === "booked") {
      toast.error("This time slot is already booked.");
      return;
    }

    const bookUrl = `/meetings/book-flow?roomId=${encodeURIComponent(selectedRoomId)}&date=${encodeURIComponent(date)}&time=${encodeURIComponent(timeKey)}`;

    const token = localStorage.getItem("token");
    if (!token) {
      localStorage.setItem("authRedirectPath", bookUrl);
      toast.info("Please log in to book this slot");
      navigate("/login");
      return;
    }

    navigate(bookUrl);
  };

  const getSlotPresentation = (dateYmd, timeKey) => {
    const status = slotStatuses.get(slotKey(dateYmd, timeKey));

    if (status === "pending") {
      return { className: "slot-pending", label: "Pending", disabled: true };
    }
    if (status === "booked") {
      return { className: "slot-booked", label: "Booked", disabled: true };
    }
    if (isPastSlot(dateYmd, timeKey)) {
      return { className: "slot-past", label: "Past", disabled: true };
    }
    return { className: "slot-available", label: "Book", disabled: false };
  };

  return (
    <div className="room-schedule-page">
      <Navbar />

      <main className="room-schedule-shell">
        <section className="room-schedule-head">
          <div>
            <h1>Room Booking Availability</h1>
            <p>
              7-day view — Pending until owner approves; approved slots show Booked
            </p>
          </div>
          <div className="room-schedule-controls">
            <span>{monthTitle}</span>
          </div>
        </section>

        <section className="room-schedule-toolbar">
          <div className="room-schedule-room-picker">
            <label htmlFor="room-select">Room</label>
            <select
              id="room-select"
              value={selectedRoomId}
              onChange={(e) => setSelectedRoomId(e.target.value)}
            >
              {rooms.length === 0 ? (
                <option value="">No rooms available</option>
              ) : (
                rooms.map((room) => (
                  <option key={room._id} value={room._id}>
                    {room.name}
                  </option>
                ))
              )}
            </select>
          </div>

          <div className="room-schedule-week-nav">
            <button type="button" onClick={() => moveWeek(-1)} disabled={!canPrevWeek}>
              Previous week
            </button>
            <span>{weekRangeLabel}</span>
            <button type="button" onClick={() => moveWeek(1)}>
              Next week
            </button>
          </div>

          <div className="room-schedule-legend">
            <div className="legend-item">
              <span className="legend-box available" />
              <span>Book</span>
            </div>
            <div className="legend-item">
              <span className="legend-box booked" />
              <span>Booked</span>
            </div>
            <div className="legend-item">
              <span className="legend-box pending" />
              <span>Pending</span>
            </div>
            <div className="legend-item">
              <span className="legend-box past" />
              <span>Past</span>
            </div>
          </div>
        </section>

        <section className="room-schedule-grid-wrap">
          {loading && slotStatuses.size === 0 ? (
            <div className="schedule-loading">Loading availability...</div>
          ) : !selectedRoom ? (
            <div className="schedule-loading">Please select a room.</div>
          ) : (
            <div className="room-schedule-grid-scroll" ref={gridScrollRef}>
              <div className="schedule-grid">
                <div className="schedule-grid-header">
                  <div className="schedule-time-head" aria-hidden="true" />
                  {displayedDates.map((dateItem) => (
                    <div
                      key={dateItem.value}
                      className={`schedule-day-head ${dateItem.value === todayYmd ? "is-today" : ""}`}
                    >
                      <small>{dateItem.dayName}</small>
                      <span>{dateItem.shortLabel}</span>
                    </div>
                  ))}
                </div>

                <div className="schedule-grid-body">
                  {TIME_SLOTS.map((slot) => (
                    <div className="schedule-grid-row" key={slot.key}>
                      <div className="schedule-time-cell">{slot.label}</div>
                      {displayedDates.map((dateItem) => {
                        const presentation = getSlotPresentation(dateItem.value, slot.key);
                        return (
                          <div
                            key={slotKey(dateItem.value, slot.key)}
                            className="schedule-slot-cell"
                          >
                            <button
                              type="button"
                              className={`slot-btn ${presentation.className}`}
                              disabled={presentation.disabled}
                              onClick={() =>
                                !presentation.disabled &&
                                handleBookClick(dateItem.value, slot.key)
                              }
                            >
                              {presentation.label}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </section>
      </main>
    </div>
  );
};

export default RoomAvailabilitySchedulePage;
